import { Link } from "react-router-dom";
import { ShieldCheck, Sparkles, GraduationCap, HandHeart, Check } from "lucide-react";
import { MaskedLines, Reveal, Overline } from "@/components/Reveal";
import { GoldButton, OutlineButton } from "@/components/Brand";
import { useEnquiry } from "@/components/EnquiryDialog";
import { TRAINERS } from "@/lib/data";

const values = [
  { title: "Safe Practice",   text: "Client safety is at the heart of everything we teach.",                           icon: ShieldCheck },
  { title: "Practical Skills",text: "We focus on developing safe, confident and competent practitioners.",             icon: Sparkles },
  { title: "Expert Education",text: "High-quality education delivered by experienced aesthetics practitioners and educators.", icon: GraduationCap },
  { title: "Ongoing Support", text: "We're here for you throughout your training journey and beyond.",                 icon: HandHeart },
];

const reasons = [
  "Over 10 years of combined aesthetics experience",
  "Hands-on practical learning",
  "Modern, welcoming learning environment",
  "Accredited CPD and regulated training pathways",
  "Live models provided",
  "Education delivered by experienced practitioners and educators",
  "Small class sizes",
  "Ongoing support after your course",
  "Focused on safety, confidence and professional standards",
];

// Large decorative AA monogram used as the About Us hero visual.
const AAMonogram = () => (
  <div
    className="relative h-[460px] md:h-[560px] overflow-hidden bg-navy border border-white/10 flex items-center justify-center"
    data-testid="about-hero-monogram"
  >
    {/* radial glow */}
    <div className="absolute inset-0 opacity-70"
         style={{ background: "radial-gradient(ellipse at center, rgba(201,168,118,0.18) 0%, rgba(10,20,32,0) 60%)" }} />
    {/* subtle grid lines */}
    <div className="absolute inset-0 opacity-[0.04]"
         style={{ backgroundImage: "linear-gradient(rgba(201,168,118,1) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,118,1) 1px, transparent 1px)", backgroundSize: "80px 80px" }} />
    {/* Monogram (outlined gold, low opacity) */}
    <svg
      viewBox="0 0 480 300"
      className="relative w-[85%] max-w-[520px] text-gold"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ opacity: 0.55 }}
      aria-hidden="true"
    >
      {/* First A */}
      <path d="M20 260 L120 40 L220 260" />
      <path d="M62 170 L178 170" />
      {/* Second A, slightly overlapping */}
      <path d="M240 260 L340 40 L440 260" />
      <path d="M282 170 L398 170" />
      {/* Base underline */}
      <path d="M40 285 L440 285" strokeWidth="1" opacity="0.6" />
    </svg>
    {/* Vertical wordmark */}
    <div
      className="absolute right-6 top-8 bottom-8 flex flex-col items-center justify-between text-[10px] tracking-[0.4em] uppercase text-gold/70"
      style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
    >
      <span>The Aesthetics Academy Co.</span>
      <span>Est. 2026</span>
    </div>
    <div className="absolute bottom-6 left-6 text-[10px] tracking-[0.35em] uppercase text-white/40" data-testid="about-monogram-tag">
      Advancing Skills &nbsp;·&nbsp; Elevating Careers
    </div>
  </div>
);

export default function About() {
  const { openEnquiry } = useEnquiry();
  return (
    <>
      <section className="relative pt-40 md:pt-52 pb-20 md:pb-28 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <Overline className="mb-5 block">About Us</Overline>
            <MaskedLines
              className="font-playfair uppercase leading-[0.95] tracking-tight"
              lineClassName="text-6xl md:text-7xl text-gold"
              lines={[<span key="1">About Us</span>]}
            />
            <div className="hairline w-40 my-8" />
            <h2 className="font-playfair text-2xl md:text-3xl text-white leading-snug mb-6">
              Education built on experience. Training built on trust.
            </h2>
            <p className="text-white/60 leading-relaxed max-w-lg mb-5">
              The Aesthetics Academy Co. brings together the experience of Edwina Evans, founder of Bonds Aesthetics, and Charlotte Thompson, founder of CLouise Aesthetics. As experienced Advanced Aesthetics Practitioners and Educators, we understand first-hand the realities of working within this fast-moving industry.
            </p>
            <p className="text-white/60 leading-relaxed max-w-lg">
              At The Aesthetics Academy Co., we believe exceptional aesthetics education is about far more than simply learning a treatment. It&apos;s about developing safe, knowledgeable and confident practitioners who have the skills, understanding and support to build long-term, successful careers.
            </p>
          </div>
          <Reveal delay={0.2}>
            <AAMonogram />
          </Reveal>
        </div>
      </section>

      <section className="py-24 bg-navy-surface border-y border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-14">
          <Reveal>
            <Overline>Our Mission</Overline>
            <h3 className="font-playfair text-3xl md:text-4xl text-white mt-4 leading-tight">
              A modern training academy committed to excellence, safety and professional standards.
            </h3>
            <div className="hairline w-24 mt-8" />
          </Reveal>
          <Reveal delay={0.15} className="space-y-5 text-white/60 leading-relaxed">
            <p>Founded by two experienced Advanced Aesthetics Practitioners and Educators, we understand the realities of working within this fast-moving industry. Through years of professional practice, education and mentoring, we&apos;ve seen first-hand that while qualifications are important, it&apos;s high-quality training, practical experience and ongoing support that truly develop confident and competent practitioners.</p>
            <p>Our vision is to build an academy that delivers education to the highest professional standard while providing every learner with the personalised support they deserve. We are passionate about sharing our knowledge, raising standards across the aesthetics industry and promoting safe, ethical and evidence-informed practice in everything we teach.</p>
            <p>Alongside our accredited CPD training, we are proud to be expanding our education offering to include nationally recognised regulated qualifications. Our aim is to provide clear progression pathways, allowing learners to develop their practical skills through accredited CPD training and progress into recognised qualifications that support their ongoing professional and career development.</p>
            <p>We deliberately keep our class sizes small, allowing every learner to receive individual guidance, constructive feedback and the time needed to build confidence. We&apos;re not simply teaching treatments, we&apos;re supporting learners to develop into safe, skilled and professional aesthetics practitioners.</p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-navy">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-white/10">
          {values.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.1} className="p-10 text-center">
              <v.icon size={40} strokeWidth={1} className="text-gold mx-auto mb-5" />
              <h4 className="font-manrope text-sm tracking-[0.15em] uppercase text-white mb-3">{v.title}</h4>
              <p className="text-white/50 text-sm leading-relaxed">{v.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-24 bg-navy-surface border-y border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal className="mb-14">
            <Overline>Why Choose</Overline>
            <h2 className="font-playfair text-4xl md:text-6xl text-white mt-4">The Aesthetics Academy Co</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-6">
            {reasons.map((r, i) => (
              <Reveal key={r} delay={i * 0.05} className="flex items-start gap-4">
                <span className="mt-0.5 w-6 h-6 shrink-0 rounded-full border border-gold/60 flex items-center justify-center">
                  <Check size={13} strokeWidth={2} className="text-gold" />
                </span>
                <span className="text-white/75">{r}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-28 bg-navy">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal className="mb-16"><Overline>Meet Your Trainers</Overline></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {TRAINERS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.15}>
                <div className="flex flex-col sm:flex-row gap-8">
                  <div className="relative w-full sm:w-56 h-72 shrink-0 overflow-hidden">
                    <img
                      src={t.image}
                      alt={t.name}
                      className={`w-full h-full object-cover ${
                        t.name === "Charlotte Thompson" ? "object-[center_20%]" : "object-center"
                      }`}
                    />
                    <div className="absolute inset-0 bg-navy/25" />
                  </div>
                  <div>
                    <h3 className="font-playfair text-3xl text-gold mb-1">{t.name}</h3>
                    {t.title && (
                      <p className="text-[11px] tracking-[0.2em] uppercase text-white/60">{t.title}</p>
                    )}
                    {t.subtitle && (
                      <p className="text-[11px] tracking-[0.2em] uppercase text-white/45 mt-1">{t.subtitle}</p>
                    )}
                    <div className="w-14 h-px bg-gold/50 mt-5 mb-5" />
                    <div className="space-y-4 text-white/65 text-sm leading-relaxed">
                      {t.bio.map((p, k) => <p key={k}>{p}</p>)}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-navy-surface border-t border-white/10 text-center">
        <div className="max-w-2xl mx-auto px-6">
          <Reveal>
            <h2 className="font-playfair text-3xl md:text-5xl text-white mb-6">More than a training academy</h2>
            <p className="text-white/60 mb-10">Whether you&apos;re taking your first steps into aesthetics, developing your existing skills or progressing towards a recognised qualification, we&apos;re here to support you at every stage of your training journey.</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/courses"><GoldButton data-testid="about-explore-button">Explore Courses</GoldButton></Link>
              <OutlineButton onClick={() => openEnquiry()} data-testid="about-enquire-button">Get In Touch</OutlineButton>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
